/**
 * useAppPermissions Hook
 * 
 * Provides access to the current user's permissions and roles for apps.
 * Useful for checking roleId, role name, and access permissions.
 * 
 * @example
 * const { getAppRole, getRoleId, hasAccess } = useAppPermissions();
 * 
 * // Check if user has access to devices-management
 * if (hasAccess(19)) { ... }
 * 
 * // Get role ID for devices-management app
 * const roleId = getRoleId(19);
 * 
 * // Get full role info
 * const role = getAppRole(19);
 */

import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../services/api/client';

// =============================================================================
// TYPES
// =============================================================================

export interface AppRole {
  id: number;
  roleName: string;
  description?: string;
  permissions?: string;
}

export interface AppInfo {
  id: number;
  appName: string;
  appCode: string;
  appUrl: string;
  isActive: boolean;
}

export interface UserAppPermission {
  id: number;
  userId: number;
  appId: number;
  roleId: number | null;
  /** Display name stored on user_to_apps (app-specific roles have no shared FK join) */
  roleName: string | null;
  templateId: number | null;
  app: AppInfo;
  role: AppRole | null;
}

// =============================================================================
// HOOK
// =============================================================================

export const useAppPermissions = () => {
  // Fetch user's permissions
  const { data: permissions, isLoading, error } = useQuery({
    queryKey: ['my-permissions'],
    queryFn: async () => {
      const response = await apiClient.get('/permissions/my-permissions');
      const data = response.data?.data || response.data || [];
      // Ensure we always return an array
      return Array.isArray(data) ? data : [] as UserAppPermission[];
    },
    staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    refetchOnWindowFocus: false,
  });

  /**
   * Check if user has access to an app
   * @param appId - The app ID to check
   */
  const hasAccess = (appId: number): boolean => {
    if (!permissions || !Array.isArray(permissions)) return false;
    return permissions.some(p => p.appId === appId);
  };

  /**
   * Get the user's role ID for an app
   * @param appId - The app ID
   * @returns The roleId or null if no access
   */
  const getRoleId = (appId: number): number | null => {
    if (!permissions || !Array.isArray(permissions)) return null;
    const permission = permissions.find(p => p.appId === appId);
    return permission?.roleId ?? null;
  };

  /**
   * Get the full role object for an app
   * @param appId - The app ID
   * @returns The role object or null
   */
  const getAppRole = (appId: number): AppRole | null => {
    if (!permissions || !Array.isArray(permissions)) return null;
    const permission = permissions.find(p => p.appId === appId);
    return permission?.role ?? null;
  };

  /**
   * Get the user's permission for an app
   * @param appId - The app ID
   * @returns The full permission object or null
   */
  const getAppPermission = (appId: number): UserAppPermission | null => {
    if (!permissions || !Array.isArray(permissions)) return null;
    return permissions.find(p => p.appId === appId) ?? null;
  };

  /**
   * Find app by URL
   * @param appUrl - The app URL (e.g., '/devices-management')
   * @returns The permission object or null
   */
  const getPermissionByAppUrl = (appUrl: string): UserAppPermission | null => {
    if (!permissions || !Array.isArray(permissions)) return null;
    return permissions.find(p => p.app?.appUrl === appUrl) ?? null;
  };

  /**
   * Get role ID by app URL
   * @param appUrl - The app URL (e.g., '/devices-management')
   * @returns The roleId or null
   */
  const getRoleIdByAppUrl = (appUrl: string): number | null => {
    const permission = getPermissionByAppUrl(appUrl);
    return permission?.roleId ?? null;
  };

  return {
    permissions,
    isLoading,
    error,
    hasAccess,
    getRoleId,
    getAppRole,
    getAppPermission,
    getPermissionByAppUrl,
    getRoleIdByAppUrl,
  };
};

export default useAppPermissions;

